// Subscription Configuration
// Defines plan limits, features, and pricing for the subscription system

export const PLAN_TYPES = {
  FREE: 'free',
  PRO: 'pro',
  LEAGUE: 'league',
};

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  TRIALING: 'trialing',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  INCOMPLETE: 'incomplete',
};

// Trial duration in days
export const TRIAL_DURATION_DAYS = 14;

// Plan pricing (for display purposes - actual prices are in Stripe)
export const PLAN_PRICING = {
  [PLAN_TYPES.FREE]: {
    monthly: 0,
    yearly: 0,
  },
  [PLAN_TYPES.PRO]: {
    monthly: 14.99,
    yearly: 139.99, // ~22% discount
  },
  [PLAN_TYPES.LEAGUE]: {
    monthly: 29.99,
    yearly: 279.99, // ~22% discount
  },
};

// Plan limits configuration
export const PLAN_LIMITS = {
  [PLAN_TYPES.FREE]: {
    maxLeagues: 1,
    maxPlayersPerLeague: 10,
    maxConcurrentGames: 1,
    maxGamesPerMonth: 10,
    features: {
      gameScheduling: false,
      leagueStats: true, // Basic league standings only
      individualStats: false, // Top earners, bounties - Pro/League only
      customBlindStructures: false,
      premiumSounds: false,
      customLeagueLogo: false,
      aiVoiceAnnouncer: false,
      whiteLabel: false,
      advancedAnalytics: false,
      exportData: false,
      prioritySupport: false,
    },
  },
  [PLAN_TYPES.PRO]: {
    maxLeagues: 1,
    maxPlayersPerLeague: 50,
    maxConcurrentGames: 5,
    maxGamesPerMonth: Infinity,
    features: {
      gameScheduling: true,
      leagueStats: true,
      individualStats: true, // Top earners, bounties, winnings
      customBlindStructures: true,
      premiumSounds: true,
      customLeagueLogo: true,
      aiVoiceAnnouncer: false,
      whiteLabel: false,
      advancedAnalytics: false,
      exportData: true,
      prioritySupport: true,
    },
  },
  [PLAN_TYPES.LEAGUE]: {
    maxLeagues: Infinity,
    maxPlayersPerLeague: Infinity,
    maxConcurrentGames: Infinity,
    maxGamesPerMonth: Infinity,
    features: {
      gameScheduling: true,
      leagueStats: true,
      individualStats: true, // Full individual stats
      customBlindStructures: true,
      premiumSounds: true,
      customLeagueLogo: true,
      aiVoiceAnnouncer: true,
      whiteLabel: true,
      advancedAnalytics: true,
      exportData: true,
      prioritySupport: true,
    },
  },
};

// Feature display names for UI
export const FEATURE_DISPLAY_NAMES = {
  gameScheduling: 'Game Scheduling',
  leagueStats: 'League Standings',
  individualStats: 'Individual Player Statistics',
  customBlindStructures: 'Custom Blind Structures',
  premiumSounds: 'Premium Sound Library',
  customLeagueLogo: 'Custom League Logo',
  aiVoiceAnnouncer: 'AI Voice Announcer',
  whiteLabel: 'White-Label Branding',
  advancedAnalytics: 'Advanced Analytics',
  exportData: 'Export Data',
  prioritySupport: 'Priority Support',
};

// Default subscription object for new users
export const DEFAULT_SUBSCRIPTION = {
  plan: PLAN_TYPES.FREE,
  status: SUBSCRIPTION_STATUS.ACTIVE,
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  currentPeriodStart: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  trialEnd: null,
};

// Create trial subscription object
export const createTrialSubscription = () => {
  const now = new Date();
  const trialEnd = new Date(now.getTime() + TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000);
  
  return {
    plan: PLAN_TYPES.PRO,
    status: SUBSCRIPTION_STATUS.TRIALING,
    stripeCustomerId: null,
    stripeSubscriptionId: null,
    currentPeriodStart: now,
    currentPeriodEnd: trialEnd,
    cancelAtPeriodEnd: false,
    trialEnd: trialEnd,
  };
};

// Helper function to check if a feature is available for a plan
export const canUsePlanFeature = (plan, feature) => {
  const limits = PLAN_LIMITS[plan];
  if (!limits) return false;
  return limits.features[feature] === true;
};

// Helper function to get plan limits
export const getPlanLimits = (plan) => {
  return PLAN_LIMITS[plan] || PLAN_LIMITS[PLAN_TYPES.FREE];
};

// Helper function to get the required plan for a feature
export const getRequiredPlanForFeature = (feature) => {
  // Check from lowest to highest tier
  if (PLAN_LIMITS[PLAN_TYPES.FREE].features[feature]) {
    return PLAN_TYPES.FREE;
  }
  if (PLAN_LIMITS[PLAN_TYPES.PRO].features[feature]) {
    return PLAN_TYPES.PRO;
  }
  if (PLAN_LIMITS[PLAN_TYPES.LEAGUE].features[feature]) {
    return PLAN_TYPES.LEAGUE;
  }
  return null; // Feature not available in any plan
};

// Helper to calculate trial days remaining
export const calculateTrialDaysRemaining = (trialEnd) => {
  if (!trialEnd) return 0;
  
  const trialEndDate = trialEnd instanceof Date
    ? trialEnd
    : typeof trialEnd.toDate === 'function'
      ? trialEnd.toDate()
      : new Date(trialEnd.seconds ? trialEnd.seconds * 1000 : trialEnd);
  const now = new Date();
  const diffTime = trialEndDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffDays);
};
